import React, { useState, useMemo } from 'react';
import { getCardById } from '../data/tarotCards';
import { exportAllReadings } from '../utils/export';
import './ReadingsSection.css';
import './FolderView.css';

function ReadingsSection({ folders, readings, onFolderSelect, onCreateFolder, onReadingSelect }) {
  const [isCreating, setIsCreating] = useState(false);
  const [newFolderName, setNewFolderName] = useState('');
  const [searchQuery, setSearchQuery] = useState('');
  const [showTitleMenu, setShowTitleMenu] = useState(false);

  const searchResults = useMemo(() => {
    const query = searchQuery.trim().toLowerCase();
    if (!query) return null;

    return readings.filter((reading) => {
      if (reading.title && reading.title.toLowerCase().includes(query)) return true;
      if (reading.reflection && reading.reflection.toLowerCase().includes(query)) return true;
      const spreads = reading.spreads || [];
      return spreads.some((spread) => {
        if (spread.question && spread.question.toLowerCase().includes(query)) return true;
        if (spread.interpretation && spread.interpretation.toLowerCase().includes(query)) return true;
        return (spread.cards || []).some((c) => {
          const info = getCardById(c.cardId);
          return info && info.name.toLowerCase().includes(query);
        });
      });
    }).sort((a, b) => new Date(b.createdAt) - new Date(a.createdAt));
  }, [searchQuery, readings]);

  const formatDate = (dateString) => {
    const date = new Date(dateString + 'T00:00:00');
    return date.toLocaleDateString('en-US', {
      weekday: 'short', month: 'short', day: 'numeric', year: 'numeric',
    });
  };


  const handleCreate = () => {
    if (newFolderName.trim()) {
      onCreateFolder(newFolderName.trim());
      setNewFolderName('');
      setIsCreating(false);
    }
  };

  const getReadingCount = (folderId) => {
    return readings.filter((r) => r.folderId === folderId).length;
  };

  const suggestedFolders = [
    'Daily Pulls',
    'Full Moon Readings',
    'New Moon Readings',
    'Career & Work',
    'Love & Relationships',
    'Yearly Forecast',
    'Shadow Work',
  ];

  return (
    <div className="readings-section fade-in">
      <div className="page-header-with-action">
        <div className="title-menu-anchor">
          <h1 className="page-title page-title-tappable" onClick={() => setShowTitleMenu(!showTitleMenu)}>
            My Readings
            <svg className="title-chevron" width="12" height="12" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
              <path d="m6 9 6 6 6-6" />
            </svg>
          </h1>
          {showTitleMenu && (
            <>
              <div className="title-menu-backdrop" onClick={() => setShowTitleMenu(false)} />
              <div className="title-menu">
                <button className="title-menu-item" onClick={() => { setShowTitleMenu(false); exportAllReadings(folders, readings); }}>
                  <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                    <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4" />
                    <polyline points="7 10 12 15 17 10" />
                    <line x1="12" x2="12" y1="15" y2="3" />
                  </svg>
                  Export All Readings
                </button>
              </div>
            </>
          )}
        </div>
        {!isCreating && (
          <button className="btn-small" onClick={() => setIsCreating(true)}>
            + New Folder
          </button>
        )}
      </div>

      {/* Search */}
      <div className="search-container">
        <svg className="search-icon" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
          <circle cx="11" cy="11" r="8" />
          <path d="M21 21l-4.35-4.35" />
        </svg>
        <input
          type="text"
          className="search-input"
          placeholder="Search readings..."
          value={searchQuery}
          onChange={(e) => setSearchQuery(e.target.value)}
        />
        {searchQuery && (
          <button className="search-clear" onClick={() => setSearchQuery('')}>
            <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        )}
      </div>

      {/* Search results */}
      {searchResults ? (
        <div className="readings-list">
          {searchResults.length === 0 ? (
            <div className="search-empty">
              <p>No readings match "{searchQuery}"</p>
            </div>
          ) : (
            searchResults.map((reading) => {
              const allCards = reading.spreads
                ? reading.spreads.flatMap((s) => s.cards)
                : reading.cards || [];

              return (
                <button
                  key={reading.id}
                  className="reading-item card"
                  onClick={() => onReadingSelect(reading.id, reading.folderId)}
                >
                  <div className="reading-item-header">
                    {reading.title && (
                      <h3 className="reading-title">{reading.title}</h3>
                    )}
                    <time className="reading-date">{formatDate(reading.date)}</time>
                  </div>
                  <div className="reading-cards">
                    {allCards.slice(0, 3).map((card, i) => {
                      const cardInfo = getCardById(card.cardId);
                      return (
                        <span key={i} className="reading-card-chip">
                          {cardInfo?.name || 'Unknown'}
                          {card.reversed && ' ↓'}
                        </span>
                      );
                    })}
                    {allCards.length > 3 && (
                      <span className="reading-card-chip">
                        +{allCards.length - 3} more
                      </span>
                    )}
                  </div>
                  <div className="reading-item-footer">
                    <span className="view-link">View reading →</span>
                  </div>
                </button>
              );
            })
          )}
        </div>
      ) : (
        <>

      {/* Create new folder form */}
      {isCreating && (
        <div className="create-folder-form card">
          <input
            type="text"
            placeholder="Folder name..."
            value={newFolderName}
            onChange={(e) => setNewFolderName(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
            autoFocus
          />
          <div className="create-folder-suggestions">
            <span className="suggestions-label">Suggestions:</span>
            {suggestedFolders
              .filter((name) => !folders.some((f) => f.name === name))
              .slice(0, 4)
              .map((name) => (
                <button
                  key={name}
                  className="suggestion-chip"
                  onClick={() => setNewFolderName(name)}
                >
                  {name}
                </button>
              ))}
          </div>
          <div className="create-folder-actions">
            <button onClick={() => { setIsCreating(false); setNewFolderName(''); }}>
              Cancel
            </button>
            <button className="btn-primary" onClick={handleCreate} disabled={!newFolderName.trim()}>
              Create Folder
            </button>
          </div>
        </div>
      )}

      {/* Empty state */}
      {folders.length === 0 && !isCreating && (
        <div className="empty-state">
          <div className="empty-icon">
            <svg width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
              <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
            </svg>
          </div>
          <h3>Organize Your Readings</h3>
          <p>Create folders to organize your tarot readings by type, purpose, or any way that works for you.</p>
          <button className="btn-primary" onClick={() => setIsCreating(true)}>
            Create Your First Folder
          </button>
        </div>
      )}

      {/* Folder list */}
      <div className="folder-list">
        {folders.map((folder) => (
          <div key={folder.id} className="folder-item card">
              <>
                <button className="folder-content" onClick={() => onFolderSelect(folder.id)}>
                  <div className="folder-icon">
                    <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                      <path d="M22 19a2 2 0 0 1-2 2H4a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h5l2 3h9a2 2 0 0 1 2 2z" />
                    </svg>
                  </div>
                  <div className="folder-info">
                    <h3 className="folder-name">{folder.name}</h3>
                    <span className="folder-count">
                      {getReadingCount(folder.id)} {getReadingCount(folder.id) === 1 ? 'reading' : 'readings'}
                    </span>
                  </div>
                  <svg className="folder-arrow" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                    <path d="M9 18l6-6-6-6" />
                  </svg>
                </button>
              </>
          </div>
        ))}
      </div>
        </>
      )}
    </div>
  );
}

export default ReadingsSection;
